/**
 * Email forwarding — REQUIREMENTS.md §6.1, the highest-priority capture path
 * because it's the one that already works today.
 *
 * Everything here is pure string handling over untrusted input. An inbound
 * email is the only part of this system a stranger can address directly, so
 * each function assumes hostile input rather than a well-formed message from a
 * cooperative client.
 */

/**
 * The address a user forwards receipts to.
 *
 * Per-user and unguessable, not a shared `receipts@` mailbox. The address *is*
 * the routing credential: anything delivered to it lands in someone's review
 * queue, and a guessable address lets a stranger fill that queue with
 * documents Matt then has to read and delete. The token below is the only
 * thing standing between the public MX record and his inbox, which is why the
 * trusted-sender list is a second, separate check rather than the first one.
 */
const TOKEN_ALPHABET = 'abcdefghijkmnpqrstuvwxyz23456789'
const TOKEN_LENGTH = 16

/**
 * Generate a routing token.
 *
 * The alphabet omits `l`, `o`, `0`, and `1` — the address gets read aloud and
 * retyped, and those four are where transcription goes wrong. 16 characters of
 * a 32-symbol alphabet is 80 bits, which is not guessable by anyone who has to
 * send an email per attempt.
 */
export function generateIngestToken(
  randomBytes: (length: number) => Uint8Array,
): string {
  const bytes = randomBytes(TOKEN_LENGTH)
  let token = ''
  for (const byte of bytes) {
    token += TOKEN_ALPHABET[byte % TOKEN_ALPHABET.length]
  }
  return token
}

export function isValidIngestToken(token: string): boolean {
  if (token.length !== TOKEN_LENGTH) return false
  return [...token].every((character) => TOKEN_ALPHABET.includes(character))
}

/**
 * Build the forwarding address for a token.
 *
 * A subdomain, not a plus-address on the main one: plus-addressing is stripped
 * or rewritten by enough mail systems that it can't be relied on for routing,
 * and a dedicated subdomain keeps the receipts MX record away from whatever
 * the apex domain is doing.
 */
export function buildIngestAddress(token: string, domain: string): string {
  return `r-${token}@${domain}`
}

/**
 * Recover the routing token from a delivered-to address.
 *
 * Returns null for anything that isn't one of our addresses, including the
 * bare domain, a wrong prefix, or a malformed token — every one of which is
 * something a scanner will try.
 */
export function ingestTokenFromAddress(address: string): string | null {
  const bare = bareEmailAddress(address)
  if (bare === null) return null

  const [localPart] = bare.split('@')
  if (localPart === undefined) return null
  if (!localPart.startsWith('r-')) return null

  const token = localPart.slice(2)
  return isValidIngestToken(token) ? token : null
}

/**
 * Extract the address from a header value.
 *
 * `From` and `To` arrive as `Display Name <someone@example.com>`, as a bare
 * address, or as a comma-separated list. Only the first address is returned —
 * a forwarded receipt has one meaningful sender, and treating a header full of
 * addresses as several senders is how an allowlist gets tricked.
 */
export function bareEmailAddress(header: string): string | null {
  const first = header.split(',')[0]?.trim() ?? ''
  if (first === '') return null

  const angled = /<([^>]+)>/.exec(first)
  const candidate = (angled?.[1] ?? first).trim().toLowerCase()

  // Deliberately strict rather than RFC-complete: one @, no whitespace, a dot
  // in the domain. Anything stranger than that is not a receipt forward.
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(candidate)) return null
  return candidate
}

/** The domain half of an address, lowercased. */
export function emailDomain(address: string): string | null {
  const bare = bareEmailAddress(address)
  return bare?.split('@')[1] ?? null
}

/**
 * Whether a sender is on the trusted list — §6.1.
 *
 * Entries are either a full address (`receipts@amazon.com`) or a whole domain
 * written `@amazon.com`. Anything not matched still gets ingested; it's flagged
 * for confirmation rather than dropped, because a receipt that silently
 * vanishes is worse than one that needs a click. Dropping is reserved for mail
 * that fails the token check.
 */
export function isTrustedSender(
  from: string,
  trustedSenders: readonly string[],
): boolean {
  const sender = bareEmailAddress(from)
  if (sender === null) return false
  const domain = sender.split('@')[1] ?? ''

  return trustedSenders.some((entry) => {
    const trimmed = entry.trim().toLowerCase()
    if (trimmed === '') return false
    if (trimmed.startsWith('@')) return domain === trimmed.slice(1)
    return sender === trimmed
  })
}

/** Normalize a trusted-sender entry as typed into the settings form. */
export function normalizeTrustedSender(entry: string): string | null {
  const trimmed = entry.trim().toLowerCase()
  if (trimmed === '') return null

  if (trimmed.startsWith('@')) {
    const domain = trimmed.slice(1)
    return /^[^\s@]+\.[^\s@]+$/.test(domain) ? trimmed : null
  }
  return bareEmailAddress(trimmed)
}

const FORWARD_PREFIX = /^\s*(?:(?:re|fwd?|fw|aw|tr)\s*:\s*)+/i

/**
 * Strip the `Fwd:`/`Re:` chain from a subject.
 *
 * Matt forwards receipts from his mail client, so nearly every subject arrives
 * prefixed, sometimes several deep. The subject is shown in the review queue
 * to help him recognize a receipt at a glance, and "Fwd: Fwd: Re: Your order"
 * helps less than "Your order".
 */
export function cleanEmailSubject(subject: string): string {
  let previous = subject
  let cleaned = subject.replace(FORWARD_PREFIX, '')
  // Repeated because a single pass leaves "Fwd: Re: x" partly prefixed when
  // the client used a separator the pattern doesn't fold in one go.
  while (cleaned !== previous) {
    previous = cleaned
    cleaned = cleaned.replace(FORWARD_PREFIX, '')
  }
  return cleaned.trim()
}

/**
 * Pick the attachment most likely to be the receipt.
 *
 * A forwarded receipt email carries the real document plus whatever the sender
 * decorated the message with — logos, tracking pixels, social icons. Those are
 * images too, so "has an attachment" is not enough; the choice is by type and
 * size, preferring a PDF and otherwise the largest image, since decoration is
 * uniformly small.
 */
export interface EmailAttachment {
  filename: string
  contentType: string
  sizeBytes: number
}

/** Below this, an image is almost certainly a logo or a spacer, not a receipt. */
const DECORATION_MAX_BYTES = 8 * 1024

export function pickReceiptAttachment<T extends EmailAttachment>(
  attachments: readonly T[],
): T | null {
  const largestFirst = (candidates: readonly T[]): T | undefined =>
    [...candidates].sort((a, b) => b.sizeBytes - a.sizeBytes)[0]

  // Largest, not first. Taking the first PDF in document order chose a 12 KB
  // `privacy-notice.pdf` over the invoice listed after it, and a boilerplate
  // page carries no merchant and no total — so the receipt reached the review
  // queue needing every field typed, with the real invoice unread inside the
  // stored message. The size floor applies here for the same reason it
  // applies to images: decoration is uniformly small.
  const pdfs = attachments.filter(
    (a) => a.contentType === 'application/pdf' || /\.pdf$/i.test(a.filename),
  )
  const pdf =
    largestFirst(pdfs.filter((a) => a.sizeBytes > DECORATION_MAX_BYTES)) ??
    // A genuinely tiny PDF is still better than nothing when it is all that
    // came: a one-line receipt can be a few kilobytes.
    largestFirst(pdfs)
  if (pdf !== undefined) return pdf

  const image = largestFirst(
    attachments
      .filter((a) => a.contentType.startsWith('image/'))
      .filter((a) => a.sizeBytes > DECORATION_MAX_BYTES),
  )

  return image ?? null
}
